/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { withConfigContext } from '../../components/ConfigContext';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Col,
  Form,
  Image,
  Input,
  Modal,
  Row,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CameraOutlined,
  InfoCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import ThemeToggle from '../../components/ThemeToggle';
import CameraCapture from './components/CameraCapture';
import PageLoading from '../../components/PageLoading';
import logo from '/public/images/logoonly.png';
import styles from './styles.module.css';

export const RoutesContext = React.createContext();

const { Title, Text } = Typography;

const getReadingLabel = (reading, index) =>
  reading?.register || `Register ${index + 1}`;

const getReadingDescription = (reading) => {
  const previousReading = reading?.previousReading;

  return [previousReading && `Previous ${previousReading}`]
    .filter(Boolean)
    .join(' | ');
};

const getMeterList = (selfReadingInfo) => {
  if (Array.isArray(selfReadingInfo)) {
    return selfReadingInfo;
  }

  if (Array.isArray(selfReadingInfo?.meters)) {
    return selfReadingInfo.meters;
  }

  if (selfReadingInfo) {
    return [selfReadingInfo];
  }

  return [];
};

const getMeterReadings = (meter) =>
  Array.isArray(meter?.readings) ? meter.readings : [];

const getReadingImageKey = (meterIndex, readingIndex) =>
  `${meterIndex}-${readingIndex}`;

const dataUrlToFile = (dataUrl, fileName) => {
  const [header, base64Data] = dataUrl.split(',');
  const mimeType = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], fileName, { type: mimeType });
};

const SelfMeterReading = ({ context, t }) => {
  const { requestId } = useParams();
  const [form] = Form.useForm();
  const uploadInputRefs = useRef({});
  const readingImagesRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [instructionsVisible, setInstructionsVisible] = useState(false);
  const [error, setError] = useState('');
  const [selfReadingInfo, setSelfReadingInfo] = useState(null);
  const [readingImages, setReadingImages] = useState({});
  const [cameraImageKey, setCameraImageKey] = useState('');
  const [previewImage, setPreviewImage] = useState('');

  const SelfMeterReadingUrls = useMemo(
    () => ({
      selfReadingInfo:
        window.location.origin +
        context.serverConfig.invoker.uri +
        '/api/meter-reading/self-reading/request',
      submitSelfReading:
        window.location.origin +
        context.serverConfig.invoker.uri +
        '/api/meter-reading/self-reading',
    }),
    [context.serverConfig.invoker.uri],
  );

  useEffect(() => {
    fetchSelfReadingInfo();
  }, [requestId]);

  useEffect(() => {
    readingImagesRef.current = readingImages;
  }, [readingImages]);

  useEffect(() => {
    return () => {
      Object.values(readingImagesRef.current).forEach((image) => {
        if (image?.previewUrl) {
          URL.revokeObjectURL(image.previewUrl);
        }
      });
    };
  }, []);

  const fetchSelfReadingInfo = () => {
    setLoading(true);
    setError('');
    setSubmitted(false);
    axios
      .get(SelfMeterReadingUrls.selfReadingInfo, {
        params: { requestId: requestId },
      })
      .then((response) => {
        setSelfReadingInfo(response.data);
        setLoading(false);
      })
      .catch((error) => {
        setError(
          error?.response?.data?.message ||
            error?.message ||
            'Unable to load the self meter reading request.',
        );
        setLoading(false);
      });
  };

  const meters = getMeterList(selfReadingInfo);
  const readingCount = meters.reduce((count, meter) => {
    return count + getMeterReadings(meter).length;
  }, 0);

  const getRequiredImageKeys = () =>
    meters.flatMap((meter, meterIndex) =>
      getMeterReadings(meter).map((reading, readingIndex) =>
        getReadingImageKey(meterIndex, readingIndex),
      ),
    );

  const getSelfReadingPayload = (formValues) => {
    const requestSummary = Array.isArray(selfReadingInfo)
      ? selfReadingInfo[0]
      : selfReadingInfo;
    return {
      billingCycle: requestSummary?.billingCycle,
      accountNo: requestSummary?.accountNo,
      myLecoUserId: 'Web App User',
      meters: (formValues?.meters || []).map((meter) => ({
        meterSerial: meter.meterSerial,
        readingMissed: false,
        reason: null,
        readings: (meter.readings || []).map((reading) => ({
          register: reading.register,
          reading: Number(reading.reading),
          previousReading: null,
          allowReadingIssue: null,
          isMeterTurnOver: null,
        })),
      })),
      capturedTime: Date.now(),
    };
  };

  const setReadingImageFile = (imageKey, file) => {
    const imageFieldName = ['images', imageKey];

    setReadingImages((previousImages) => {
      if (previousImages[imageKey]?.previewUrl) {
        URL.revokeObjectURL(previousImages[imageKey].previewUrl);
      }

      return {
        ...previousImages,
        [imageKey]: {
          file,
          previewUrl: URL.createObjectURL(file),
          error: '',
        },
      };
    });

    form.setFieldValue(imageFieldName, file);
    form.setFields([
      {
        name: imageFieldName,
        errors: [],
      },
    ]);
  };

  const onImageChange = (event, imageKey) => {
    const file = event.target.files?.[0];
    const imageFieldName = ['images', imageKey];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setReadingImages((previousImages) => {
        if (previousImages[imageKey]?.previewUrl) {
          URL.revokeObjectURL(previousImages[imageKey].previewUrl);
        }

        return {
          ...previousImages,
          [imageKey]: {
            file: null,
            previewUrl: '',
            error: 'Please upload a valid image file.',
          },
        };
      });
      form.setFieldValue(imageFieldName, undefined);
      form.setFields([
        {
          name: imageFieldName,
          errors: ['Please upload a valid image file.'],
        },
      ]);
      return;
    }

    setReadingImageFile(imageKey, file);
    event.target.value = '';
  };

  const onCaptureImage = (dataUrl) => {
    if (!cameraImageKey) {
      return;
    }

    setReadingImageFile(
      cameraImageKey,
      dataUrlToFile(dataUrl, `meter-reading-${cameraImageKey}.jpg`),
    );
    setCameraImageKey('');
  };

  const onDeleteImage = (imageKey) => {
    const imageFieldName = ['images', imageKey];

    setReadingImages((previousImages) => {
      if (previousImages[imageKey]?.previewUrl) {
        URL.revokeObjectURL(previousImages[imageKey].previewUrl);
      }

      return {
        ...previousImages,
        [imageKey]: {
          file: null,
          previewUrl: '',
          error: 'A meter reading image is required.',
        },
      };
    });
    form.setFieldValue(imageFieldName, undefined);
    form.setFields([
      {
        name: imageFieldName,
        errors: ['A meter reading image is required.'],
      },
    ]);
  };

  const onSubmit = async () => {
    try {
      const formValues = await form.validateFields();

      const missingImageKeys = getRequiredImageKeys().filter(
        (imageKey) => !readingImages[imageKey]?.file,
      );

      if (missingImageKeys.length > 0) {
        setReadingImages((previousImages) => {
          const nextImages = { ...previousImages };

          getRequiredImageKeys().forEach((imageKey) => {
            nextImages[imageKey] = {
              ...nextImages[imageKey],
              error: missingImageKeys.includes(imageKey)
                ? 'A meter reading image is required.'
                : '',
            };
          });

          return nextImages;
        });
        return;
      }

      setReadingImages((previousImages) => {
        const nextImages = { ...previousImages };

        getRequiredImageKeys().forEach((imageKey) => {
          nextImages[imageKey] = {
            ...nextImages[imageKey],
            error: '',
          };
        });

        return nextImages;
      });

      const formData = new FormData();
      const selfReading = getSelfReadingPayload(formValues);

      const dataPart = new File(
        [JSON.stringify(selfReading)],
        'selfReading.json',
        {
          type: 'application/json',
        },
      );
      formData.append('selfReading', dataPart);

      getRequiredImageKeys().forEach((imageKey) => {
        const imageFile = readingImages[imageKey]?.file;

        if (imageFile) {
          formData.append('image', imageFile);
        }
      });

      setSubmitting(true);
      await axios.post(SelfMeterReadingUrls.submitSelfReading, formData);
      setSubmitted(true);
    } catch (validationError) {
      setReadingImages((previousImages) => {
        const nextImages = { ...previousImages };

        getRequiredImageKeys().forEach((imageKey) => {
          nextImages[imageKey] = {
            ...nextImages[imageKey],
            error: nextImages[imageKey]?.file
              ? ''
              : 'A meter reading image is required.',
          };
        });

        return nextImages;
      });
      if (!validationError?.errorFields) {
        message.error(
          validationError?.response?.data?.message ||
            validationError?.message ||
            'Unable to submit meter reading details.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.centerState}>
          <PageLoading message="Loading meter reading request..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.centerState}>
          <Alert
            className={styles.errorAlert}
            type="error"
            showIcon
            message="Unable to load meter reading request"
            description={error}
          />
        </div>
      );
    }

    return (
      <main className={styles.content}>
        <div className={styles.formShell}>
          <div className={styles.headerBlock}>
            <div>
              <Title level={2} className={styles.title}>
                Self Meter Reading
              </Title>
              <Text className={styles.subtitle}>
                Enter each register reading and attach a clear meter image.
              </Text>
            </div>
            <Button
              type="primary"
              icon={<InfoCircleOutlined />}
              onClick={() => setInstructionsVisible(true)}
            >
              How to submit a self reading
            </Button>
          </div>

          <Row gutter={[16, 16]} className={styles.summaryRow}>
            <Col xs={24} md={12}>
              <div className={styles.summaryItem}>
                <span>Account Number</span>
                <strong>
                  {(Array.isArray(selfReadingInfo)
                    ? selfReadingInfo[0]?.accountNo
                    : selfReadingInfo?.accountNo) ?? ''}
                </strong>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className={styles.summaryItem}>
                <span>Billing Cycle</span>
                <strong>
                  {(Array.isArray(selfReadingInfo)
                    ? selfReadingInfo[0]?.billingCycle
                    : selfReadingInfo?.billingCycle) ?? ''}
                </strong>
              </div>
            </Col>
          </Row>

          {submitted ? (
            <Alert
              type="success"
              showIcon
              message="Meter reading submitted successfully"
              description="Your self meter reading details have been submitted."
            />
          ) : (
            <Form form={form} layout="vertical" className={styles.form}>
              <div className={styles.sectionHeader}>
                <h3>Meter Readings</h3>
                <span>{readingCount} required</span>
              </div>

              {readingCount > 0 ? (
                <Row gutter={[16, 16]}>
                  {meters.map((data, meterIndex) => (
                    <Col xs={24} md={24} key={data.meterSerial || meterIndex}>
                      <div className={styles.meterGroup}>
                        <div className={styles.meterTitle}>
                          <span>Meter Serial : </span>
                          <strong>
                            <Form.Item
                              name={['meters', meterIndex, 'meterSerial']}
                              initialValue={data.meterSerial}
                            >
                              <Input
                                disabled
                                className={styles.disabledInputValue}
                              />
                            </Form.Item>
                          </strong>
                        </div>

                        {getMeterReadings(data).map((reading, readingIndex) => {
                          const imageKey = getReadingImageKey(
                            meterIndex,
                            readingIndex,
                          );
                          const readingImage = readingImages[imageKey] || {};
                          const register = reading.register || '';

                          return (
                            <div
                              className={styles.readingCard}
                              key={
                                reading.id ||
                                reading.code ||
                                reading.register ||
                                readingIndex
                              }
                            >
                              <Form.Item
                                hidden
                                name={[
                                  'meters',
                                  meterIndex,
                                  'readings',
                                  readingIndex,
                                  'register',
                                ]}
                                initialValue={register}
                              >
                                <Input />
                              </Form.Item>
                              <div className={styles.readingEntryRow}>
                                <Form.Item
                                  label="Register"
                                  extra={getReadingDescription(reading)}
                                >
                                  <Input
                                    readOnly
                                    disabled={true}
                                    className={styles.disabledInputValue}
                                    value={getReadingLabel(
                                      reading,
                                      readingIndex,
                                    )}
                                  />
                                </Form.Item>

                                <Form.Item
                                  name={[
                                    'meters',
                                    meterIndex,
                                    'readings',
                                    readingIndex,
                                    'reading',
                                  ]}
                                  label="Reading Value"
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Reading value is required.',
                                    },
                                    {
                                      validator: (_, value) => {
                                        if (
                                          value === undefined ||
                                          value === null ||
                                          value === ''
                                        ) {
                                          return Promise.resolve();
                                        }

                                        const readingValue = Number(value);

                                        if (Number.isNaN(readingValue)) {
                                          return Promise.reject(
                                            new Error(
                                              'Reading value must be a number.',
                                            ),
                                          );
                                        }

                                        if (readingValue < 0) {
                                          return Promise.reject(
                                            new Error(
                                              'Reading value cannot be negative.',
                                            ),
                                          );
                                        }

                                        return Promise.resolve();
                                      },
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>

                                <Form.Item
                                  name={['images', imageKey]}
                                  label="Image"
                                  rules={[
                                    {
                                      validator: () => {
                                        if (readingImage.file) {
                                          return Promise.resolve();
                                        }

                                        return Promise.reject(
                                          new Error(
                                            readingImage.error ||
                                              'A meter reading image is required.',
                                          ),
                                        );
                                      },
                                    },
                                  ]}
                                >
                                  {readingImage.file ? (
                                    <Upload
                                      // listType="picture"
                                      maxCount={1}
                                      fileList={[
                                        {
                                          uid: imageKey,
                                          name:
                                            readingImage.file.name ||
                                            'meter-reading-image',
                                          status: 'done',
                                          url: readingImage.previewUrl,
                                          thumbUrl: readingImage.previewUrl,
                                        },
                                      ]}
                                      showUploadList={{
                                        showDownloadIcon: false,
                                        showPreviewIcon: true,
                                        showRemoveIcon: true,
                                      }}
                                      onPreview={() =>
                                        setPreviewImage(readingImage.previewUrl)
                                      }
                                      onRemove={() => {
                                        onDeleteImage(imageKey);
                                        return false;
                                      }}
                                    />
                                  ) : (
                                    <div className={styles.imageActions}>
                                      <Tooltip title="Upload image">
                                        <Button
                                          icon={<UploadOutlined />}
                                          onClick={() =>
                                            uploadInputRefs.current[
                                              imageKey
                                            ]?.click()
                                          }
                                          className={styles.iconActionBtn}
                                          aria-label="Upload image"
                                        />
                                      </Tooltip>
                                      <Tooltip title="Capture image">
                                        <Button
                                          icon={<CameraOutlined />}
                                          onClick={() =>
                                            setCameraImageKey(imageKey)
                                          }
                                          className={styles.iconActionBtn}
                                          aria-label="Capture image"
                                        />
                                      </Tooltip>
                                    </div>
                                  )}

                                  <input
                                    ref={(element) => {
                                      uploadInputRefs.current[imageKey] =
                                        element;
                                    }}
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    aria-hidden="true"
                                    tabIndex={-1}
                                    onChange={(event) =>
                                      onImageChange(event, imageKey)
                                    }
                                  />
                                </Form.Item>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message="No meter registers found for this request."
                />
              )}

              <div className={styles.submitRow}>
                <Button
                  type="primary"
                  className={styles.primaryBtn}
                  onClick={onSubmit}
                  disabled={readingCount === 0 || submitting}
                  loading={submitting}
                >
                  Submit Reading
                </Button>
              </div>
            </Form>
          )}
        </div>
      </main>
    );
  };

  return (
    <div className={styles.page}>
      <Row className={styles.navRow} align={'middle'} justify="space-between">
        <div className={styles.logoContainer}>
          <img src={logo} alt="Logo" className={styles.logoImg} />
          <div className={styles.logoText}>
            <span className={styles.lecoText}>LECO</span>
            <span className={styles.companyName}>Self Meter Reading</span>
          </div>
        </div>
        <ThemeToggle t={t} />
      </Row>
      {renderContent()}
      <Modal
        title="How to submit a self reading"
        open={instructionsVisible}
        footer={null}
        onCancel={() => setInstructionsVisible(false)}
      >
        <ol className={styles.instructionsList}>
          <li>
            Confirm that the Account Number and Meter Serial Number shown on the
            screen belong to your electricity meter.
          </li>
          <li>
            Enter the current reading for each register displayed on the screen.
            <ul>
              <li>Complete the Reading Value field for every register.</li>
              <li>Enter only the numbers shown on your meter.</li>
              <li>Do not include units or extra symbols.</li>
            </ul>
          </li>
          <li>
            Upload or take a clear photo for each register reading.
            <div>Make sure:</div>
            <ul>
              <li>The reading is clearly visible.</li>
              <li>The photo is not blurry.</li>
              <li>The entire meter display can be seen.</li>
            </ul>
          </li>
          <li>
            Check that each reading you entered matches its photo, then click
            Submit Reading.
          </li>
        </ol>
      </Modal>
      <Modal
        title="Capture meter image"
        open={Boolean(cameraImageKey)}
        footer={null}
        width={720}
        destroyOnClose
        onCancel={() => setCameraImageKey('')}
      >
        <CameraCapture onCapture={onCaptureImage} />
      </Modal>
      <Image
        alt="Meter reading preview"
        src={previewImage}
        style={{ display: 'none' }}
        preview={{
          visible: Boolean(previewImage),
          src: previewImage,
          onVisibleChange: (visible) => {
            if (!visible) {
              setPreviewImage('');
            }
          },
        }}
      />
    </div>
  );
};

SelfMeterReading.contextType = RoutesContext;
export default withConfigContext(withTranslation()(SelfMeterReading));
